// Platform-wide banner -- one message shown to every tenant at once (e.g.
// "maintenance tonight 11pm-1am"), not per-tenant. See migration 0104 for
// the table/RLS -- it's a singleton row, and tenants only ever see it
// while is_active is true, so drafting a message doesn't publish it.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Megaphone, Save } from 'lucide-react';
import { logPlatformAdminAction } from '@/lib/platformAuditLog';

interface AnnouncementSettings {
  message: string | null;
  severity: 'info' | 'warning' | 'critical';
  is_active: boolean;
}

const SEVERITY_STYLES: Record<AnnouncementSettings['severity'], string> = {
  info: 'border-blue-900/40 bg-blue-900/10 text-blue-400',
  warning: 'border-yellow-900/40 bg-yellow-900/10 text-yellow-500',
  critical: 'border-red-900/40 bg-red-900/10 text-red-400',
};

const SuperadminAnnouncement: React.FC = () => {
  const [settings, setSettings] = useState<AnnouncementSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('platform_announcement')
        .select('message, severity, is_active')
        .eq('id', true)
        .maybeSingle();
      if (error) {
        toast({ title: 'Error', description: 'Failed to load announcement: ' + error.message, variant: 'destructive' });
      } else if (data) {
        setSettings(data as AnnouncementSettings);
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('platform_announcement')
      .update({
        message: settings.message,
        severity: settings.severity,
        is_active: settings.is_active,
        updated_at: new Date().toISOString(),
        updated_by: userData.user?.id ?? null,
      })
      .eq('id', true);
    setSaving(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to save announcement: ' + error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Saved', description: settings.is_active ? 'Announcement is now live for every tenant.' : 'Announcement saved (not shown -- turned off).' });
    logPlatformAdminAction('announcement_updated', 'platform_announcement', null, {
      is_active: settings.is_active,
      severity: settings.severity,
    });
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;
  if (!settings) return <p className="text-sm text-muted-foreground">Announcement settings unavailable.</p>;

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-yellow-500" />
            Platform Announcement
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Shown to every signed-in user across every tenant -- for things like scheduled maintenance, not tenant-specific
            news.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-yellow-400 text-xs font-medium">Message</Label>
            <Textarea
              className="dark-input min-h-24"
              value={settings.message ?? ''}
              onChange={(e) => setSettings({ ...settings, message: e.target.value })}
              placeholder="e.g. Scheduled maintenance tonight 11pm-1am -- the app may be briefly unavailable."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-yellow-400 text-xs font-medium">Severity</Label>
              <Select value={settings.severity} onValueChange={(v) => setSettings({ ...settings, severity: v as AnnouncementSettings['severity'] })}>
                <SelectTrigger className="dark-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black border-yellow-600">
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/20 px-3">
              <span className="text-sm text-foreground">Live for all tenants</span>
              <Switch checked={settings.is_active} onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })} />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="golden-button">
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </CardContent>
      </Card>

      {settings.is_active && settings.message && (
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5">Preview</p>
          <div className={`rounded-lg border px-4 py-3 text-sm ${SEVERITY_STYLES[settings.severity]}`}>{settings.message}</div>
        </div>
      )}
    </div>
  );
};

export default SuperadminAnnouncement;
